import {
  CheckCircleIcon,
  CopySimpleIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';

import { ICON_WEIGHT, type IconComponent } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { aircraft } from '@/lib/db';
import { formatBytes } from '@/lib/format';

import type { QueueItem, QueueItemStatus } from './queue';
import { useImportQueue } from './queue';

/**
 * The per-file result list.
 *
 * This is the deliverable `PLAN.md` §6 actually names for Phase 1 — "a per-file
 * result list" — and it is the part that makes bulk import trustworthy. Fifty
 * files where three were unreadable and two were already in the database is a
 * normal outcome, and unless the list says which, the pilot has no way to know
 * what happened to their folder.
 *
 * So every row states its own outcome in plain words, a skipped duplicate reads
 * as information rather than as a failure, and nothing clears itself.
 */

const STATUS: Record<
  QueueItemStatus,
  { label: string; icon: IconComponent; tone: 'pending' | 'good' | 'info' | 'bad'; spin?: boolean }
> = {
  queued: { label: 'Waiting', icon: SpinnerGapIcon, tone: 'pending' },
  reading: { label: 'Reading', icon: SpinnerGapIcon, tone: 'pending', spin: true },
  parsing: { label: 'Reading track', icon: SpinnerGapIcon, tone: 'pending', spin: true },
  saving: { label: 'Saving', icon: SpinnerGapIcon, tone: 'pending', spin: true },
  imported: { label: 'Imported', icon: CheckCircleIcon, tone: 'good' },
  duplicate: { label: 'Already have it', icon: CopySimpleIcon, tone: 'info' },
  failed: { label: 'Could not read', icon: WarningCircleIcon, tone: 'bad' },
};

const TONE: Record<'pending' | 'good' | 'info' | 'bad', string> = {
  pending: 'text-muted-foreground',
  good: 'text-accent',
  info: 'text-muted-foreground',
  bad: 'text-danger',
};

function QueueRow({ item }: { item: QueueItem }) {
  const status = STATUS[item.status];
  const Icon = status.icon;

  return (
    <li
      /*
       * The status as an attribute, not only as a label. A test that matched
       * the words would also match "Already imported as ..." in a duplicate
       * row's explanation, and quietly count twice.
       */
      data-testid="queue-item"
      data-status={item.status}
      className="border-border flex gap-3 border-b px-4 py-3 last:border-b-0"
    >
      <Icon
        aria-hidden="true"
        weight={ICON_WEIGHT}
        className={`mt-0.5 size-4 shrink-0 ${TONE[status.tone]} ${
          status.spin === true ? 'motion-safe:animate-spin' : ''
        }`}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-medium" title={item.filename}>
            {item.filename}
          </p>
          <span className={`shrink-0 text-xs ${TONE[status.tone]}`}>{status.label}</span>
        </div>

        {item.message === undefined ? null : (
          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">{item.message}</p>
        )}

        {item.detail.length === 0 ? null : (
          <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
            {item.detail.map((line) => (
              <li key={line} className="truncate" title={line}>
                {line}
              </li>
            ))}
          </ul>
        )}

        <p className="text-muted-foreground mt-1 text-xs">
          <span className="tabular">{formatBytes(item.sizeBytes)}</span>
          {item.track === undefined ? null : (
            <>
              {' · '}
              <span className="tabular">{item.track.pointCount}</span> fixes
              {item.track.registration === undefined ? null : ` · ${item.track.registration}`}
            </>
          )}
        </p>
      </div>
    </li>
  );
}

function NextSteps({ items }: { items: readonly QueueItem[] }) {
  const known = useLiveQuery(
    async () => new Set((await aircraft.list()).map((entry) => entry.registration)),
    [],
    null,
  );
  const registrations = [
    ...new Set(
      items.flatMap((item) =>
        item.status === 'imported' && item.track?.registration !== undefined
          ? [item.track.registration]
          : [],
      ),
    ),
  ];
  const missing = known === null ? [] : registrations.filter((reg) => !known.has(reg));

  return (
    <div className="border-border flex flex-wrap items-center gap-2 border-t px-4 py-2.5">
      <Button size="sm" asChild>
        <Link to="/logbook">Review in logbook</Link>
      </Button>
      {missing.slice(0, 3).map((registration) => (
        <Button key={registration} size="sm" variant="outline" asChild>
          <Link to="/aircraft/new" search={{ registration }}>
            Add {registration}
          </Link>
        </Button>
      ))}
    </div>
  );
}

export function ImportQueuePanel() {
  const items = useImportQueue((state) => state.items);
  const busy = useImportQueue((state) => state.busy);
  const offThread = useImportQueue((state) => state.offThread);
  const clearFinished = useImportQueue((state) => state.clearFinished);

  if (items.length === 0) {
    return null;
  }

  const finished = items.filter((item) => item.status !== 'queued').length;
  const importedCount = items.filter((item) => item.status === 'imported').length;
  const failed = items.filter((item) => item.status === 'failed').length;

  return (
    <aside
      aria-label="Import results"
      data-testid="import-queue"
      /*
       * A block in the page on a phone, a floating card from `sm` up.
       *
       * It used to be `fixed` at every width, which on a phone meant a
       * full-width sheet across the bottom of the window — over the top of the
       * buttons belonging to the flight that had just been imported. A fixed
       * sheet does not scroll with the page, so those buttons could not be
       * reached at all, and `e2e/map.spec.ts` and `e2e/export.spec.ts` failed on
       * the mobile project with the sheet's own list intercepting the click.
       * Rendered in the flow — inside `main`, after the outlet — it takes its own
       * space below the page instead. The desktop card is unchanged.
       */
      className="border-border bg-surface rounded-container z-(--z-overlay) mx-auto mt-6 flex max-h-[60vh] w-full flex-col border shadow-lg sm:fixed sm:right-4 sm:bottom-4 sm:left-auto sm:mt-0 sm:w-[26rem]"
    >
      <header className="border-border flex items-center gap-3 border-b px-4 py-3">
        <h2 className="flex-1 text-sm font-semibold tracking-tight">
          {busy ? (
            <>
              Importing <span className="tabular">{finished}</span> of{' '}
              <span className="tabular">{items.length}</span>
            </>
          ) : (
            <>
              <span className="tabular">{items.length}</span> file{items.length === 1 ? '' : 's'}
              {failed > 0 ? (
                <>
                  , <span className="tabular">{failed}</span> unreadable
                </>
              ) : null}
            </>
          )}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Dismiss import results"
          disabled={busy}
          onClick={clearFinished}
        >
          <XIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
        </Button>
      </header>

      {/*
        Assistive technology gets the running total rather than fifty rows
        announcing themselves one at a time.
      */}
      <p aria-live="polite" className="sr-only">
        {busy
          ? `Importing file ${String(finished)} of ${String(items.length)}.`
          : `Import finished. ${String(items.length)} files, ${String(failed)} unreadable.`}
      </p>

      <ul className="flex-1 overflow-y-auto">
        {items.map((item) => (
          <QueueRow key={item.id} item={item} />
        ))}
      </ul>

      {/*
        Where to go next. An import drafts a logbook entry per flight and
        stops there, and nothing on the Flights page used to say there was a
        step left — nor that a registration it had just read was not yet an
        aircraft on file.
      */}
      {busy || importedCount === 0 ? null : <NextSteps items={items} />}

      {offThread ? null : (
        <p className="border-border text-muted-foreground border-t px-4 py-2 text-xs">
          Reading files on the main thread. This browser did not start a background worker, so a
          large batch may be slower.
        </p>
      )}
    </aside>
  );
}
